import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal, Input, Button } from 'common/components';
import { emailRegisterOptions } from 'common/constants';
import { useInviteMember } from 'services/hooks/mutations';
import { hasAnyRole, nonEmptyProducts } from '../../products';
import { ProductRolesPicker } from '../ProductRolesPicker';
import { memberErrorMessage } from '../memberErrors';
import { InviteUserModalProps, InviteUserForm } from './InviteUserModal.types';

// Invites a person: Cognito mails them a temporary password; they are
// "pending" until their first sign-in. Roles by product.
const InviteUserModal: React.FC<InviteUserModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onError,
    accountId,
    companyName,
    manageable,
}) => {
    const {
        register,
        handleSubmit: handleFormSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<InviteUserForm>({
        defaultValues: { firstName: '', lastName: '', email: '', products: {} },
    });

    const { inviteMember, isPending } = useInviteMember();

    useEffect(() => {
        if (!isOpen) reset();
    }, [isOpen, reset]);

    const onSubmit = async (data: InviteUserForm) => {
        try {
            await inviteMember({
                accountId,
                data: {
                    first_name: data.firstName,
                    last_name: data.lastName,
                    email: data.email.trim().toLowerCase(),
                    products: nonEmptyProducts(data.products),
                },
            });
            onClose();
            onSuccess('Invitation email sent. The user will set their own password.');
        } catch (error: any) {
            console.error('Error inviting user:', error);
            onError(memberErrorMessage(error, 'Failed to invite user. Please try again.', { emailTaken: true }));
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={companyName ? `Invite User to ${companyName}` : 'Invite User'}
            size='md'
            showDefaultFooter={false}
        >
            <form onSubmit={handleFormSubmit(onSubmit)} id='invite-user-form' className='flex flex-col gap-md'>
                <Input
                    label='First Name'
                    type='text'
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                    {...register('firstName', { required: 'First name is required' })}
                />
                <Input
                    label='Last Name'
                    type='text'
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                    {...register('lastName', { required: 'Last name is required' })}
                />
                <Input
                    label='Email'
                    type='email'
                    error={!!errors.email}
                    errorText={errors.email?.message}
                    {...register('email', emailRegisterOptions)}
                />
                <p className='text-sm text-gray-600'>
                    An invitation email will be sent via Cognito. The user sets their own password.
                </p>
                <Controller
                    name='products'
                    control={control}
                    rules={{ validate: (v) => hasAnyRole(v) || 'Give at least one role' }}
                    render={({ field }) => (
                        <ProductRolesPicker
                            manageable={manageable}
                            value={field.value}
                            onChange={field.onChange}
                            errorText={errors.products?.message as string | undefined}
                        />
                    )}
                />
                <div className='flex justify-end gap-md mt-xl'>
                    <Button onClick={handleClose} variant='secondary'>
                        Cancel
                    </Button>
                    <Button type='submit' disabled={isPending}>
                        Invite User
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default InviteUserModal;
